import { rateLineListQuerySchema, rateLineProposalListQuerySchema } from '@chukua/common';
import { useEffect, useState } from 'react';
import { buildLineHistoryChain } from '../policies/build-line-history-chain';
import { listCardProposals, listRateLines, type RateLineProposalView, type RateLineView } from '../../../rate-cards.api';

export interface LineVersion {
  line: RateLineView;
  /** The approved proposal that produced this line, which carries when it was decided and why. */
  approval: RateLineProposalView | undefined;
}

export type LineHistoryState = { status: 'loading' } | { status: 'loaded'; versions: LineVersion[] } | { status: 'error' };

const MAX_PAGE_SIZE = 100;

/**
 * Every version of `line`'s price, oldest first. The Lines tab is paged, so the other versions may not be on screen:
 * this asks for the lines sharing its lane and job type (cargo and basis are told apart by following the supersede
 * pointers), then for the approved proposals behind exactly those lines, in one call.
 */
export function useLineHistory(line: RateLineView | null): LineHistoryState {
  // The outcome is kept with the line it answers, so opening another line reads as loading without resetting state in the effect.
  const [result, setResult] = useState<{ line: RateLineView; state: LineHistoryState } | null>(null);

  useEffect(() => {
    if (!line) return;
    let cancelled = false;

    async function load(target: RateLineView) {
      const sameLane = await listRateLines(
        target.rateCardId,
        rateLineListQuerySchema.parse({
          originZoneCode: [target.originZoneCode],
          destinationZoneCode: [target.destinationZoneCode],
          jobTypeCode: [target.jobTypeCode],
          sort: 'validFrom',
          order: 'asc',
          pageSize: MAX_PAGE_SIZE,
        }),
      );
      const chain = buildLineHistoryChain(target, sameLane.items);
      const approvals = await listCardProposals(
        target.rateCardId,
        rateLineProposalListQuerySchema.parse({ status: ['approved'], rateLineId: chain.map((version) => version.id), pageSize: MAX_PAGE_SIZE }),
      );
      const byLineId = new Map(approvals.items.map((proposal) => [proposal.rateLineId, proposal]));
      return chain.map((version) => ({ line: version, approval: byLineId.get(version.id) }));
    }

    load(line)
      .then((versions) => {
        if (!cancelled) setResult({ line, state: { status: 'loaded', versions } });
      })
      .catch(() => {
        if (!cancelled) setResult({ line, state: { status: 'error' } });
      });

    return () => {
      cancelled = true;
    };
  }, [line]);

  return result !== null && result.line === line ? result.state : { status: 'loading' };
}
